<template>
  <div class="layout-navbars-breadcrumb-user" :style="{ flex: layoutUserFlexNum }">
    <div class="layout-navbars-breadcrumb-user-icon" title="刷新" @click="refreshPage">
      <el-icon><RefreshRight /></el-icon>
    </div>
    <div class="layout-navbars-breadcrumb-user-icon" title="菜单搜索" @click="openSearch">
      <el-icon><Search /></el-icon>
    </div>

    <div class="layout-navbars-breadcrumb-user-icon">
      <el-dropdown trigger="click" :hide-on-click="true" placement="bottom-end" @visible-change="onNoticeVisible">
        <span class="notice-trigger">
          <el-icon><Bell /></el-icon>
          <i v-if="dealtList.length > 0" class="icon-tip" />
        </span>
        <template #dropdown>
          <el-dropdown-menu class="noticedrop">
            <el-card v-if="dealtList.length > 0" class="notice-card" shadow="never">
              <template #header>
                <span>系统通知</span>
              </template>
              <router-link
                v-for="(item, i) in dealtList"
                :key="i"
                class="item_content"
                :to="noticePath(item.path)"
              >
                <div class="title">{{ item.title }}</div>
                <div class="message">{{ item.message }}</div>
              </router-link>
              <div
                v-if="todoList.length > 3 && todoList.length !== dealtList.length"
                class="moreBtn"
                @click.stop="expandTodoList"
              >
                展开全部
                <el-icon><ArrowDown /></el-icon>
              </div>
            </el-card>
            <el-card v-else class="notice-card" shadow="never">
              <template #header>
                <span>系统通知</span>
              </template>
              <div class="tab-empty">
                <div class="empty-text">暂无系统通知</div>
              </div>
            </el-card>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </div>

    <div class="layout-navbars-breadcrumb-user-icon" :title="isFullscreen ? '退出全屏' : '全屏'" @click="toggleFullscreen">
      <el-icon>
        <FullScreen v-if="!isFullscreen" />
        <Crop v-else />
      </el-icon>
    </div>

    <div v-if="merLabel.merName" class="layout-navbars-breadcrumb-user-icon mer-name-wrap" :title="merLabel.merName">
      <div class="platform-label">{{ merLabel.merName }}</div>
    </div>

    <el-dropdown class="user-account-dropdown" trigger="click" @command="onDropdownCommand">
      <span class="layout-navbars-breadcrumb-user-link" :title="merLabel.merAccount">
        <span class="user-account-text">{{ merLabel.merAccount }}</span>
        <el-icon class="el-icon--right"><ArrowDown /></el-icon>
      </span>
      <template #dropdown>
        <el-dropdown-menu>
          <el-dropdown-item command="user">个人中心</el-dropdown-item>
          <el-dropdown-item command="store">店铺信息</el-dropdown-item>
          <el-dropdown-item command="password" divided>修改密码</el-dropdown-item>
          <el-dropdown-item command="logout" divided>退出登录</el-dropdown-item>
        </el-dropdown-menu>
      </template>
    </el-dropdown>

    <div class="layout-navbars-breadcrumb-user-icon" title="布局配置" @click="themeStore.openSettingsDrawer()">
      <el-icon><Setting /></el-icon>
    </div>

    <header-search ref="searchRef" />
  </div>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, onMounted, reactive, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  ArrowDown,
  Bell,
  Crop,
  FullScreen,
  RefreshRight,
  Search,
  Setting,
} from '@element-plus/icons-vue';
import { useFullscreen } from '@vueuse/core';
import { ElMessage, ElMessageBox } from 'element-plus';
import Cookies from 'js-cookie';
import { needDealtList } from '@/api/system';
import { editFormApi, getBaseInfo, passwordFormApi } from '@/api/user';
import useUserStore from '@/store/modules/user';
import useCrmebThemeConfigStore from '@/store/modules/themeConfig';
import { ensureMerchantPrefix } from '@/utils/crmebMenu';
import  {roterPre} from "@/settings";
import HeaderSearch from './HeaderSearch.vue';

interface TodoItem {
  path?: string;
  title?: string;
  message?: string;
}

const router = useRouter();
const route = useRoute();
const userStore = useUserStore();
const themeStore = useCrmebThemeConfigStore();
const { proxy } = getCurrentInstance()!;
const { isFullscreen, toggle: toggleFullscreen } = useFullscreen();

/** 对应 EB user.vue layoutUserFlexNum：defaults/classic/columns 为 1，顶栏变窄时由本栏吃剩余宽度 */
const layoutUserFlexNum = computed(() => {
  const { layout, isClassicSplitMenu } = themeStore.themeConfig;
  if (
    layout === 'defaults' ||
    (layout === 'classic' && !isClassicSplitMenu) ||
    layout === 'columns'
  ) {
    return 1;
  }
  return undefined;
});

const searchRef = ref<InstanceType<typeof HeaderSearch> | null>(null);
const merLabel = reactive({
  merName: '',
  merAccount: '',
});
const todoList = ref<TodoItem[]>([]);
const dealtList = ref<TodoItem[]>([]);

/** 从 info / MerInfo 解析顶栏账号（merAccount 可能是管理员对象，勿直接插值） */
function resolveMerAccountDisplay(info: Record<string, unknown> | null | undefined): string {
  if (!info) return '';
  const mer = info.merAccount;
  if (typeof mer === 'string') {
    const s = mer.trim();
    if (s && !s.startsWith('{') && !s.startsWith('[')) return s;
  }
  if (mer && typeof mer === 'object') {
    const o = mer as Record<string, unknown>;
    const account = o.account ?? o.merAccount;
    if (account != null && account !== '' && typeof account !== 'object') return String(account);
    if (o.phone != null && o.phone !== '') return String(o.phone);
  }
  const admin = info.merAdminInfo as Record<string, unknown> | undefined;
  if (admin?.account != null && admin.account !== '') return String(admin.account);
  if (info.account != null && info.account !== '') return String(info.account);
  return '';
}

function resolveMerNameDisplay(info: Record<string, unknown> | null | undefined): string {
  if (!info) return '';
  return String(info.merName ?? info.storeName ?? '');
}

function applyMerInfo(info: Record<string, unknown> | null | undefined) {
  if (!info) return;
  const name = resolveMerNameDisplay(info);
  if (name) merLabel.merName = name;
  merLabel.merAccount = resolveMerAccountDisplay(info) || userStore.name || '账号';
}

function readMerFromCookie() {
  try {
    const raw = Cookies.get('MerInfo');
    if (!raw) return;
    applyMerInfo(JSON.parse(raw) as Record<string, unknown>);
  } catch {
    merLabel.merAccount = userStore.name || '账号';
  }
}

function loadMerInfo() {
  getBaseInfo()
    .then((res: any) => {
      const data = res?.data ?? res;
      applyMerInfo(data);
      try {
        Cookies.set('MerInfo', JSON.stringify(data));
      } catch {
        /* ignore */
      }
    })
    .catch(() => readMerFromCookie());
}

function getTodoList() {
  needDealtList()
    .then((res: any) => {
      const list = res?.data ?? res ?? [];
      todoList.value = Array.isArray(list) ? list : [];
      dealtList.value =
        todoList.value.length > 3 ? todoList.value.slice(0, 3) : todoList.value;
    })
    .catch(() => {
      todoList.value = [];
      dealtList.value = [];
    });
}

function onNoticeVisible(open: boolean) {
  if (open) getTodoList();
}

function expandTodoList() {
  dealtList.value = [...todoList.value];
}

function noticePath(path?: string) {
  if (!path) return roterPre || '/merchant';
  return ensureMerchantPrefix(path);
}

function openSearch() {
  searchRef.value?.openSearch();
}

/** 刷新当前路由页面（非整页 reload，对齐 EB bus.onTagsViewRefreshRouterView） */
function refreshPage() {
  const tab = (proxy as { $tab?: { refreshPage: (obj?: object) => Promise<unknown> } }).$tab;
  if (!tab?.refreshPage) return;
  tab.refreshPage({
    name: route.name,
    path: route.path,
    query: { ...route.query },
    meta: route.meta,
  }).catch((err: unknown) => {
    console.warn('[UserBar] refreshPage', err);
  });
}

function onDropdownCommand(cmd: string) {
  const modalForm = (proxy as { $modalForm?: (fn: () => Promise<unknown>) => Promise<unknown> })
    .$modalForm;
  if (cmd === 'logout') {
    ElMessageBox.confirm('此操作将退出登录，是否继续？', '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning',
    }).then(() => {
      userStore.logout().then(() => {
        window.location.href = '/login';
      });
    });
    return;
  }
  if (cmd === 'password' && modalForm) {
    modalForm(() => passwordFormApi());
    return;
  }
  if (cmd === 'user' && modalForm) {
    modalForm(() => editFormApi());
    return;
  }
  if (cmd === 'store') {
    router.push(`${roterPre}/systemForm/modifyStoreInfo`);
  }
}

onMounted(() => {
  readMerFromCookie();
  loadMerInfo();
  getTodoList();
});
</script>

<style scoped lang="scss">
/* 对齐 EB mer layout/navBars/breadcrumb/user.vue */
.layout-navbars-breadcrumb-user {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 15px;
  height: 50px;
  min-width: 0;
  overflow: hidden;

  .layout-navbars-breadcrumb-user-icon {
    flex-shrink: 0;
    padding: 0 10px;
    cursor: pointer;
    color: var(--prev-bg-topBarColor, #515a6e);
    height: 50px;
    line-height: 50px;
    display: flex;
    align-items: center;
    font-size: 18px;
    &:hover {
      background: var(--prev-color-hover, rgba(0, 0, 0, 0.04));
    }
    .notice-trigger {
      position: relative;
      display: flex;
      align-items: center;
    }
    .icon-tip {
      position: absolute;
      background: #f56464;
      width: 6px;
      height: 6px;
      border-radius: 100%;
      top: 12px;
      right: 2px;
    }
  }

  /* 商家名：有空间时随文字变宽；顶栏变窄时只缩这一块（对齐 EB platformLabel，无 80px 上限） */
  .layout-navbars-breadcrumb-user-icon.mer-name-wrap {
    flex: 0 1 auto;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
  }
  /* 对齐 EB .platformLabel；裁切不加点，胶囊随容器连续变窄 */
  .platform-label {
    display: inline-block;
    background: var(--prev-color-primary, #4073fa);
    color: #fff;
    vertical-align: text-bottom;
    font-size: 12px;
    padding: 0 8px;
    height: 26px;
    line-height: 26px;
    border-radius: 10px;
    position: relative;
    max-width: 100%;
    min-width: 28px;
    box-sizing: border-box;
    white-space: nowrap;
    overflow: hidden;
  }

  .user-account-dropdown {
    flex-shrink: 0;
  }
  .layout-navbars-breadcrumb-user-link {
    cursor: pointer;
    color: var(--prev-bg-topBarColor, #515a6e);
    display: flex;
    align-items: center;
    height: 100%;
    font-size: 14px;
    padding: 0 4px;
    white-space: nowrap;
    min-width: 0;
    max-width: 100%;
  }
  .user-account-text {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .el-icon--right {
    flex-shrink: 0;
  }
}

:deep(.noticedrop.el-dropdown-menu) {
  padding: 0;
}
.notice-card {
  width: 240px;
  border: none;
  :deep(.el-card__header) {
    padding: 10px 16px 0;
    font-weight: 600;
    border: none;
  }
  :deep(.el-card__body) {
    padding: 0 16px 12px;
  }
}
.item_content {
  display: block;
  margin-top: 10px;
  line-height: 20px;
  font-size: 13px;
  text-decoration: none;
  .title {
    color: #333;
    font-weight: 600;
  }
  .message {
    color: #666;
  }
}
.moreBtn {
  color: #666;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-top: 8px;
  cursor: pointer;
}
.tab-empty {
  text-align: center;
  padding: 16px 0;
  .empty-text {
    color: #999;
    font-size: 12px;
  }
}
</style>
